import { PageHeader, LoadingState, ActionToolbar } from '../components/AdminPageUI';
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  Card,
  CardContent,
  Grid,
  Divider,
  CircularProgress,
  IconButton,
  Stack,
  Container,
  Alert,
  Chip,
  Paper,
  useMediaQuery,
  InputAdornment
} from "@mui/material";
import { useTheme, alpha } from '@mui/material/styles';
import { PhotoCamera, Edit, Save, Business, ContactMail, AssignmentInd } from "@mui/icons-material";
import api from "../utils/Api";

function SchoolInfoPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [schoolInfo, setSchoolInfo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    address: "",
    phone: "",
    email: "",
    motto: "",
    headTeacherName: "",
  });

  // Fetch school info
  useEffect(() => {
    // console.log("Fetching school info...");
    setIsLoading(true);
    api
      .get("/school-info")
      .then((res) => {
        // console.log("Fetched school info:", res.data);
        setSchoolInfo(res.data);
        setFormData({
          address: res.data.address || "",
          phone: res.data.phone || "",
          email: res.data.email || "",
          motto: res.data.motto || "",
          headTeacherName: res.data.headTeacherName || "",
        });
        setLogoPreview(res.data.logo || null);
        setSignaturePreview(res.data.headTeacherSignature || null);
      })
      .catch((err) => {
        console.error("Error fetching school info:", err);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleChange = (e) => {
    // console.log(`Field changed: ${e.target.name} = ${e.target.value}`);
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (!files || !files[0]) return;

    const file = files[0];
    if (!file.type.startsWith("image/")) {
      alert("Please upload an image file");
      return;
    }

    if (name === "signature") {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);

          // Background Removal Logic:
          // Iterate through pixels and make everything that isn't dark "transparent"
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;

          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];

            // Calculate brightness/luminance
            // If the pixel is relatively bright (closer to white/brown/grey paper), make it transparent
            // Threshold of 160 is a good balance for typical paper backgrounds
            const brightness = (r + g + b) / 3;
            if (brightness > 165) {
              data[i + 3] = 0; // Set Alpha to 0 (Transparent)
            }
            // Else: Keep original color (r, g, b)
          }

          ctx.putImageData(imageData, 0, 0);

          // Convert to PNG blob
          canvas.toBlob((blob) => {
            const pngFile = new File([blob], "signature.png", { type: "image/png" });
            setSignatureFile(pngFile);
            setSignaturePreview(URL.createObjectURL(pngFile));
          }, "image/png");
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    } else {
      const reader = new FileReader();
      reader.onload = () => {
        if (name === "logo") setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      if (name === "logo") setLogoFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // console.log("Submitting form with data:", formData);
    // console.log("Logo file:", logoFile);
    // console.log("Signature file:", signatureFile);


    setIsLoading(true);
    setIsUploading(true);

    try {
      const data = new FormData();
      data.append("address", formData.address);
      data.append("phone", formData.phone);
      data.append("email", formData.email);
      data.append("motto", formData.motto);
      data.append("headTeacherName", formData.headTeacherName);

      if (logoFile) {
        // console.log("Appending logo file to FormData");
        data.append("logo", logoFile);
      }
      if (signatureFile) {
        // console.log("Appending signature file to FormData");
        data.append("signature", signatureFile);
      }

      const res = await api.post("/school-info", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // console.log("Save response:", res.data);


      // Update local state with latest data from backend
      setSchoolInfo(res.data);
      setFormData({
        address: res.data.address || "",
        phone: res.data.phone || "",
        email: res.data.email || "",
        motto: res.data.motto || "",
        headTeacherName: res.data.headTeacherName || "",
      });

      // Reset file previews and file inputs
      setLogoPreview(res.data.logo || null);
      setSignaturePreview(res.data.headTeacherSignature || null);
      setLogoFile(null);
      setSignatureFile(null);

      setIsEditing(false);

      // Optional: alert for successful save
      if (res.data.warnings?.length) {
        console.warn("Warnings:", res.data.warnings);
      } else {
        // console.log("School info saved successfully.");
      }
    } catch (err) {
      console.error("Error saving school info:", err);
      alert("Failed to save school information. Check console logs.");
    } finally {
      setIsUploading(false);
      setIsLoading(false);
    }
  };

  if (isLoading && !schoolInfo) {
    return (
      <LoadingState />
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      {/* Header */}
      <PageHeader title={<>School Information</>} description={<>Manage your school's profile and contact details</>} actions={<>{!isEditing ? <Button variant="contained" startIcon={<Edit />} onClick={() => setIsEditing(true)} disabled={isLoading} sx={{
          borderRadius: 2,
          mt: isMobile ? 1 : 0,
          width: isMobile ? '100%' : 'auto'
        }} size={isMobile ? "medium" : "large"}>
                      Edit Information
                    </Button> : <Button variant="outlined" onClick={() => setIsEditing(false)} disabled={isLoading} sx={{
          borderRadius: 2,
          mt: isMobile ? 1 : 0,
          width: isMobile ? '100%' : 'auto'
        }} size={isMobile ? "medium" : "large"}>
                      Cancel
                    </Button>}</>}></PageHeader>

      {/* Main Content Card */}
      <Card sx={{
        boxShadow: isMobile ? 1 : 3,
        border: `1px solid ${theme.palette.divider}`
      }}>
        <CardContent sx={{ p: isMobile ? 2 : 4 }}>
          {/* Action Header */}
          <Stack
            direction={isMobile ? "column" : "row"}
            alignItems={isMobile ? "flex-start" : "center"}
            spacing={2}
            sx={{ mb: 4 }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{
                bgcolor: theme.palette.primary.main,
                width: isMobile ? 32 : 40,
                height: isMobile ? 32 : 40
              }}>
                <Business fontSize={isMobile ? "small" : "medium"} />
              </Avatar>
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600">
                School Profile Management
              </Typography>
            </Stack>
            <Box sx={{ flexGrow: 1 }} />
            
          </Stack>

          <form onSubmit={handleSubmit}>
            {/* UPDATED TO GRID V2 - REMOVED item PROP AND UPDATED breakpoints */}
            <Grid container spacing={isMobile ? 2 : 4}>
              {/* School Header Section */}
              <Grid size={{ xs: 12 }}>
                <Paper className="workspace-section"
                  elevation={isMobile ? 0 : 1}
                  sx={{
                    p: isMobile ? 2 : 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                  }}
                >
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    alignItems={isMobile ? "center" : "center"}
                    spacing={isMobile ? 2 : 3}
                    textAlign={isMobile ? "center" : "left"}
                  >
                    <Box position="relative">
                      <Avatar
                        src={logoPreview}
                        sx={{
                          width: isMobile ? 80 : 100,
                          height: isMobile ? 80 : 100,
                          border: `3px solid ${theme.palette.primary.main}`
                        }}
                        variant="rounded"
                      />
                      {isEditing && (
                        <IconButton
                          color="primary"
                          component="label"
                          sx={{
                            position: "absolute",
                            bottom: isMobile ? -6 : -8,
                            right: isMobile ? -6 : -8,
                            bgcolor: 'white',
                            boxShadow: 2,
                            '&:hover': { bgcolor: 'grey.50' },
                            width: isMobile ? 32 : 40,
                            height: isMobile ? 32 : 40
                          }}
                          disabled={isUploading}
                          size={isMobile ? "small" : "medium"}
                        >
                          <PhotoCamera fontSize={isMobile ? "small" : "medium"} />
                          <input type="file" name="logo" accept="image/*" onChange={handleFileChange} hidden />
                        </IconButton>
                      )}
                    </Box>
                    <Box>
                      <Typography
                        variant={isMobile ? "h4" : "h3"}
                        component="h2"
                        fontWeight="bold"
                        gutterBottom
                      >
                        {schoolInfo?.school?.name || "School Name"}
                      </Typography>
                      {schoolInfo?.motto && (
                        <Typography
                          variant="subtitle1"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          Motto: {schoolInfo.motto}
                        </Typography>
                      )}
                      <Chip
                        label="Active Institution"
                        color="success"
                        variant="outlined"
                        size={isMobile ? "small" : "medium"}
                      />
                    </Box>
                  </Stack>
                </Paper>
              </Grid>

              {/* Contact Information Section */}
              <Grid size={{ xs: 12, md: 7 }}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                      <ContactMail color="primary" fontSize={isMobile ? "small" : "medium"} />
                      <Typography variant={isMobile ? "h6" : "h6"} fontWeight="600">
                        Contact Information
                      </Typography>
                    </Stack>

                    <Grid container spacing={isMobile ? 2 : 3}>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label="School Address"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          disabled={!isEditing}
                          variant={isEditing ? "outlined" : "filled"}
                          multiline
                          rows={isMobile ? 2 : 3}
                          InputLabelProps={{
                            shrink: true,
                            sx: { fontWeight: 600, mb: 1 }
                          }}
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1
                            }
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          label="Phone Number"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          disabled={!isEditing}
                          variant={isEditing ? "outlined" : "filled"}
                          InputLabelProps={{
                            shrink: true,
                            sx: { fontWeight: 600, mb: 1 }
                          }}
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1
                            }
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          label="Email Address"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          disabled={!isEditing}
                          variant={isEditing ? "outlined" : "filled"}
                          InputLabelProps={{
                            shrink: true,
                            sx: { fontWeight: 600, mb: 1 }
                          }}
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1
                            }
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label="School Motto"
                          name="motto"
                          value={formData.motto}
                          onChange={handleChange}
                          disabled={!isEditing}
                          variant={isEditing ? "outlined" : "filled"}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">Motto:</InputAdornment>,
                          }}
                          InputLabelProps={{
                            shrink: true,
                            sx: { fontWeight: 600, mb: 1 }
                          }}
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1
                            }
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label="Head Teacher Name"
                          name="headTeacherName"
                          value={formData.headTeacherName}
                          onChange={handleChange}
                          disabled={!isEditing}
                          variant={isEditing ? "outlined" : "filled"}
                          InputLabelProps={{
                            shrink: true,
                            sx: { fontWeight: 600, mb: 1 }
                          }}
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Signature & Media Section */}
              <Grid size={{ xs: 12, md: 5 }}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', mt: isMobile ? 2 : 0 }}>
                  <CardContent sx={{ p: isMobile ? 2 : 3, height: '100%' }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                      <AssignmentInd color="primary" fontSize={isMobile ? "small" : "medium"} />
                      <Typography variant={isMobile ? "h6" : "h6"} fontWeight="600">
                        Official Media
                      </Typography>
                    </Stack>

                    {/* Head Teacher Signature */}
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                        Head Teacher Signature
                      </Typography>
                      <Paper className="workspace-section"
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.02)
                        }}
                      >
                        <Box position="relative" sx={{ width: "100%", maxWidth: 300, mx: 'auto' }}>
                          <Box
                            sx={{
                              width: "100%",
                              height: isMobile ? 100 : 120,
                              border: `2px dashed ${theme.palette.divider}`,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              backgroundColor: alpha(theme.palette.primary.main, 0.04),
                              borderRadius: 1,
                            }}
                          >
                            {signaturePreview ? (
                              <img
                                src={signaturePreview}
                                alt="Signature"
                                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: 'contain' }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary" align="center">
                                No signature uploaded
                              </Typography>
                            )}
                          </Box>
                          {isEditing && (
                            <IconButton
                              color="primary"
                              component="label"
                              sx={{
                                position: "absolute",
                                bottom: isMobile ? -6 : -8,
                                right: isMobile ? -6 : -8,
                                bgcolor: 'white',
                                boxShadow: 2,
                                '&:hover': { bgcolor: 'grey.50' },
                                width: isMobile ? 32 : 40,
                                height: isMobile ? 32 : 40
                              }}
                              disabled={isUploading}
                              size={isMobile ? "small" : "medium"}
                            >
                              <PhotoCamera fontSize={isMobile ? "small" : "medium"} />
                              <input type="file" name="signature" accept="image/*" onChange={handleFileChange} hidden />
                            </IconButton>
                          )}
                        </Box>
                      </Paper>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* School Logo */}
                    <Box>
                      <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                        School Logo
                      </Typography>
                      <Stack
                        direction={isMobile ? "column" : "row"}
                        alignItems={isMobile ? "center" : "center"}
                        spacing={2}
                        textAlign={isMobile ? "center" : "left"}
                      >
                        <Box position="relative">
                          <Avatar
                            src={logoPreview}
                            sx={{
                              width: isMobile ? 60 : 80,
                              height: isMobile ? 60 : 80,
                              border: `2px solid ${theme.palette.primary.main}`
                            }}
                            variant="rounded"
                          />
                          {isEditing && (
                            <IconButton
                              color="primary"
                              component="label"
                              sx={{
                                position: "absolute",
                                bottom: isMobile ? -6 : -8,
                                right: isMobile ? -6 : -8,
                                bgcolor: 'white',
                                boxShadow: 2,
                                '&:hover': { bgcolor: 'grey.50' },
                                width: isMobile ? 28 : 32,
                                height: isMobile ? 28 : 32
                              }}
                              disabled={isUploading}
                              size="small"
                            >
                              <PhotoCamera fontSize="small" />
                              <input type="file" name="logo" accept="image/*" onChange={handleFileChange} hidden />
                            </IconButton>
                          )}
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Recommended size: 300×300px
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Formats: PNG, JPG, SVG
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Save Buttons */}
              {isEditing && (
                <Grid size={{ xs: 12 }}>
                  <Paper className="workspace-section"
                    variant="outlined"
                    sx={{
                      p: isMobile ? 2 : 3,
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                      borderRadius: 2
                    }}
                  >
                    <ActionToolbar
                      direction={isMobile ? "column" : "row"}
                      spacing={2}
                      justifyContent="flex-end"
                      alignItems={isMobile ? "stretch" : "center"}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => setIsEditing(false)}
                        disabled={isLoading}
                        sx={{
                          borderRadius: 2,
                          minWidth: isMobile ? 'auto' : 120
                        }}
                        fullWidth={isMobile}
                        size={isMobile ? "medium" : "large"}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        startIcon={isLoading ? <CircularProgress size={20} /> : <Save />}
                        disabled={isLoading || isUploading}
                        sx={{
                          borderRadius: 2,
                          minWidth: isMobile ? 'auto' : 160
                        }}
                        fullWidth={isMobile}
                        size={isMobile ? "medium" : "large"}
                      >
                        {isLoading ? "Saving Changes..." : "Save Changes"}
                      </Button>
                    </ActionToolbar>

                    {isUploading && (
                      <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                        Uploading files... Please wait
                      </Alert>
                    )}
                  </Paper>
                </Grid>
              )}
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
}

export default SchoolInfoPage;